import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Tabs,
  Tab,
  Paper,
  Chip,
  alpha,
  Divider,
} from '@mui/material';
import {
  Close,
  SupervisorAccount,
  Phone,
  Email,
  Work,
  Star,
  WarningAmber,
  CheckCircleOutline,
  EventNote,
  MedicalServices,
  LocalOffer,
  AccessTime,
  AttachMoney,
  LocationOn,
  Groups,
  WbSunny,
  NightsStay,
  EventBusy,
  CancelOutlined,
} from '@mui/icons-material';
import ChatReservation from '../ChatReservation';
import StatusSelect from './StatusSelect';
import ConferenceBanner from './details/ConferenceBanner';
import ClientCard from './details/ClientCard';
import PractitionerCard from './details/PractitionerCard';
import ReservationReportsTab from './details/ReservationReportsTab';

// 💳 Import du composant de la commande (second fichier)
import ReservationOrderTab from './details/ReservationOrderTab';

// 🩺 Import du composant de dossier patient
import ViewPatientDialog from '../../Patients/components/ViewPatientDialog';

// ==========================================
// 📋 SOUS-COMPOSANT : DÉTAILS DE LA PRESTATION
// ==========================================
const OfferDetails = ({ infoOffre }) => {
  if (!infoOffre) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: '#FFF',
          borderRadius: '16px',
          border: '1px dashed #CBD5E1',
          textAlign: 'center',
        }}
      >
        <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Aucun détail disponible pour cette prestation.
        </Typography>
      </Paper>
    );
  }

  let joursOffArray = [];
  try {
    if (infoOffre.jours_off) {
      joursOffArray =
        typeof infoOffre.jours_off === 'string'
          ? JSON.parse(infoOffre.jours_off)
          : infoOffre.jours_off;
    }
  } catch (e) {
    joursOffArray = [];
  }

  const formatLabel =
    infoOffre.format_offre === 'presentiel'
      ? 'Présentiel'
      : infoOffre.format_offre === 'en_ligne'
        ? 'En ligne / Visio'
        : infoOffre.format_offre || 'Non spécifié';

  const locationTypeLabel =
    infoOffre.location_type === 'locaux_soa'
      ? 'Locaux SOA'
      : infoOffre.location_type === 'domicile'
        ? 'À domicile'
        : infoOffre.location_type || 'N/C';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: '18px',
        bgcolor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            bgcolor: '#F1F5F9',
            color: '#334155',
            p: 1,
            borderRadius: '10px',
            display: 'flex',
          }}
        >
          <LocalOffer fontSize="small" />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.1rem' }}
          >
            Détails de la Prestation
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            Fiche informative sur l'offre associée à la réservation
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          bgcolor: '#F8FAFC',
          borderRadius: '12px',
          border: '1px solid #E2E8F0',
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 800, color: '#1E293B', mb: 0.5 }}
        >
          {infoOffre.titre_offre || 'Prestation sans titre'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
          {infoOffre.description_offre || 'Aucune description fournie.'}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '12px',
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              height: '100%',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <AttachMoney sx={{ color: '#64748B', fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Tarif & Participation
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
              {infoOffre.prix_offre ? `${infoOffre.prix_offre}€` : 'Gratuit'}
            </Typography>
            {infoOffre.prix_special_soa && (
              <Typography
                variant="caption"
                sx={{ color: '#64748B', display: 'block', mt: 0.5 }}
              >
                Tarif spécial SOA :{' '}
                <strong style={{ color: '#1E293B' }}>
                  {infoOffre.prix_special_soa}€
                </strong>
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '12px',
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              height: '100%',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <AccessTime sx={{ color: '#64748B', fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Durée Séance
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
              {infoOffre.duree_offre ? `${infoOffre.duree_offre}min` : 'N/C'}
            </Typography>
            {infoOffre.duree_pause && (
              <Typography
                variant="caption"
                sx={{ color: '#64748B', display: 'block', mt: 0.5 }}
              >
                Intervalle / Pause :{' '}
                <strong style={{ color: '#1E293B' }}>
                  {infoOffre.duree_pause}min
                </strong>
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '12px',
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              height: '100%',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <LocationOn sx={{ color: '#64748B', fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Format & Lieu
              </Typography>
            </Stack>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: '#0F172A' }}
            >
              {formatLabel}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#64748B', display: 'block', mt: 0.5 }}
            >
              Type :{' '}
              <strong style={{ color: '#1E293B' }}>{locationTypeLabel}</strong>
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: '12px',
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              height: '100%',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <Groups sx={{ color: '#64748B', fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Public Cible
              </Typography>
            </Stack>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                color: '#0F172A',
                textTransform: 'capitalize',
              }}
            >
              {infoOffre.cible_offre || 'Tous publics'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#64748B', display: 'block', mt: 0.5 }}
            >
              Déplacement :{' '}
              <strong style={{ color: '#1E293B' }}>
                {infoOffre.is_deplacement_possible ? 'Oui' : 'Non'}
              </strong>
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {(infoOffre.address_ws || infoOffre.city_ws) && (
        <Box
          sx={{
            p: 2,
            borderRadius: '12px',
            bgcolor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <LocationOn sx={{ color: '#64748B', fontSize: 24 }} />
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: '#64748B',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              Adresse de la consultation
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#1E293B' }}
            >
              {infoOffre.address_ws || ''}
              {infoOffre.postal_code_ws ? `, ${infoOffre.postal_code_ws}` : ''}
              {infoOffre.city_ws ? ` ${infoOffre.city_ws}` : ''}
              {infoOffre.country_ws ? ` (${infoOffre.country_ws})` : ''}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2.5 }} />

      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 800, color: '#1E293B', mb: 2 }}
      >
        Plages Horaires Habituelles du Praticien
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '10px',
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <WbSunny sx={{ color: '#64748B', fontSize: 22 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: '#64748B', fontWeight: 700, display: 'block' }}
              >
                Créneau Matin
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: '#0F172A' }}
              >
                {infoOffre.plage_horaire_matin_debut || '09:00'} —{' '}
                {infoOffre.plage_horaire_matin_fin || '12:00'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '10px',
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <NightsStay sx={{ color: '#64748B', fontSize: 22 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: '#64748B', fontWeight: 700, display: 'block' }}
              >
                Créneau Après-midi
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, color: '#0F172A' }}
              >
                {infoOffre.plage_horaire_aprem_debut || '14:00'} —{' '}
                {infoOffre.plage_horaire_aprem_fin || '18:00'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '10px',
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <EventBusy sx={{ color: '#64748B', fontSize: 22 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: '#64748B', fontWeight: 700, display: 'block' }}
              >
                Jours de Repos
              </Typography>
              <Box
                sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}
              >
                {joursOffArray.length > 0 ? (
                  joursOffArray.map((jour, i) => (
                    <Chip
                      key={i}
                      label={jour}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        bgcolor: '#E2E8F0',
                        color: '#334155',
                      }}
                    />
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: '#1E293B' }}
                  >
                    Aucun
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

// ==========================================
// 🏥 COMPOSANT PRINCIPAL : RESERVATION DETAILS DIALOG
// ==========================================
const ReservationDetailsDialog = ({
  reservation,
  isIntervenant,
  isPraticien,
  apiUrl,
  token,
  onClose,
  onStatusChange,
  onSuccess,
}) => {
  const data = reservation?.originalData || reservation;
  const conference = data?.info_conference?.[0];
  const statusKey = data?.status_reservation || reservation?.statut_reservation;

  const isIntervenantUser =
    isIntervenant ||
    data?.user?.role_user === 3 ||
    data?.user?.role === 3 ||
    String(data?.user?.role_label || '')
      .toLowerCase()
      .includes('intervenant');

  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (e) {
      console.error(
        "Erreur de lecture de l'utilisateur depuis localStorage",
        e
      );
    }
  }, []);

  // Fonction de callback après mise à jour de la commande
  const handleOrderUpdated = (updatedCommand) => {
    if (reservation?.originalData?.commande) {
      reservation.originalData.commande = updatedCommand;
    }
    if (onSuccess) onSuccess();
  };

  const clientObj = data?.client || {};
  const patientProfile = clientObj?.patient_profile || {};
  const aidantsArray =
    patientProfile?.aidants || clientObj.aidants || data?.aidants || [];

  const hasAidants = Array.isArray(aidantsArray) && aidantsArray.length > 0;

  const infoOffre = data?.offre?.info_offre || {};
  const isPresentielLocauxSoa =
    infoOffre.format_offre === 'presentiel' &&
    infoOffre.location_type === 'locaux_soa';

  const rawValidated = String(data?.is_validated || '').toLowerCase();
  const isValidated =
    rawValidated === 'valide' ||
    rawValidated === 'valider' ||
    rawValidated === 'true' ||
    rawValidated === '1';

  const isRefusedByAdmin =
    rawValidated === 'refuse' ||
    rawValidated === 'refusé' ||
    statusKey === 'cancelled' ||
    statusKey === 'refuse';

  const isPendingValidation =
    isPresentielLocauxSoa && !isValidated && !isRefusedByAdmin;

  const handleClose = () => {
    if (onSuccess) onSuccess();
    if (onClose) onClose();
  };

  return (
    <>
      <Dialog
        open={!!reservation}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: '95%',
            maxWidth: '1450px',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
            backgroundImage: 'none',
            overflow: 'hidden',
          },
        }}
      >
        {reservation && (
          <>
            <DialogTitle
              sx={{
                p: { xs: 2.5, md: 3 },
                bgcolor: '#1E293B',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                borderBottom: '4px solid #3B82F6',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      bgcolor: alpha('#3B82F6', 0.2),
                      border: '1px solid #3B82F6',
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      color: '#60A5FA',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    <MedicalServices sx={{ fontSize: 16 }} />
                    Dossier Médical & Réservation
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.1rem', md: '1.35rem' },
                      color: '#F8FAFC',
                    }}
                  >
                    #{data?.id_offre_reservation}
                  </Typography>

                  {patientProfile?.code_patient && (
                    <Chip
                      label={`Code Patient : ${patientProfile.code_patient}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: '#E2E8F0',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    />
                  )}

                  {isPresentielLocauxSoa &&
                    (isRefusedByAdmin ? (
                      <Chip
                        icon={
                          <CancelOutlined
                            sx={{
                              fontSize: '16px !important',
                              color: '#EF4444 !important',
                            }}
                          />
                        }
                        label="Refusée par le Super Admin"
                        size="small"
                        sx={{
                          bgcolor: alpha('#EF4444', 0.15),
                          color: '#F87171',
                          fontWeight: 800,
                          fontSize: '0.75rem',
                          border: '1px solid #EF4444',
                        }}
                      />
                    ) : isValidated ? (
                      <Chip
                        icon={
                          <CheckCircleOutline
                            sx={{
                              fontSize: '16px !important',
                              color: '#10B981 !important',
                            }}
                          />
                        }
                        label="Validée (Locaux SOA)"
                        size="small"
                        sx={{
                          bgcolor: alpha('#10B981', 0.15),
                          color: '#34D399',
                          fontWeight: 800,
                          fontSize: '0.75rem',
                          border: '1px solid #10B981',
                        }}
                      />
                    ) : (
                      <Chip
                        icon={
                          <WarningAmber
                            sx={{
                              fontSize: '16px !important',
                              color: '#F59E0B !important',
                            }}
                          />
                        }
                        label="En attente de validation par le super admin (Présentiel SOA)"
                        size="small"
                        sx={{
                          bgcolor: alpha('#F59E0B', 0.15),
                          color: '#FBBF24',
                          fontWeight: 800,
                          fontSize: '0.75rem',
                          border: '1px dashed #F59E0B',
                        }}
                      />
                    ))}
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                  mt={0.5}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94A3B8',
                      fontSize: '0.825rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                    }}
                  >
                    <EventNote sx={{ fontSize: 16, color: '#64748B' }} />
                    Créée le{' '}
                    {data?.created_at
                      ? new Date(data.created_at).toLocaleString('fr-FR', {
                          dateStyle: 'full',
                          timeStyle: 'short',
                        })
                      : '-'}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#CBD5E1', fontWeight: 600 }}
                    >
                      Statut réservation :
                    </Typography>
                    <StatusSelect
                      value={statusKey}
                      onChange={(v) =>
                        onStatusChange(data.id_offre_reservation, v)
                      }
                      sx={{
                        minWidth: '160px',
                        bgcolor: '#0F172A',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={handleClose}
                sx={{
                  color: '#94A3B8',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  ml: 2,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#FFF',
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>

            {/* 📑 BARRE D'ONGLETS (3 ONGLETS) */}
            {!isIntervenantUser && (
              <Box
                sx={{
                  bgcolor: '#FFF',
                  borderBottom: '1px solid #E2E8F0',
                  px: 3,
                }}
              >
                <Tabs
                  value={currentTab}
                  onChange={(e, newValue) => setCurrentTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      py: 2,
                      color: '#64748B',
                      '&.Mui-selected': { color: '#2563EB' },
                    },
                    '& .MuiTabs-indicator': { bgcolor: '#2563EB', height: 3 },
                  }}
                >
                  <Tab label="Détails de la réservation" />
                  <Tab label="Comptes rendus médicaux" />
                  <Tab label="Détails de la commande" />
                </Tabs>
              </Box>
            )}

            <DialogContent
              sx={{
                p: { xs: 2.5, md: 4 },
                bgcolor: '#F8FAFC',
                maxHeight: '80vh',
                overflowY: 'auto',
              }}
            >
              {/* ONGLET 0 : DÉTAILS DE LA RÉSERVATION */}
              {(currentTab === 0 || isIntervenantUser) && (
                <Stack spacing={3.5}>
                  <Box>
                    <ConferenceBanner
                      conference={conference}
                      reservation={data}
                    />

                    {isPresentielLocauxSoa &&
                      (isRefusedByAdmin ? (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#FEF2F2',
                            border: '1px solid #EF4444',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <CancelOutlined
                            sx={{ color: '#DC2626', fontSize: 24 }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                color: '#991B1B',
                                fontSize: '0.875rem',
                                fontWeight: 800,
                              }}
                            >
                              Réservation refusée par le super admin
                            </Typography>
                            <Typography
                              sx={{
                                color: '#B91C1C',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                              }}
                            >
                              Cette demande de réservation en présentiel dans
                              les locaux SOA a été refusée par l'administration.
                            </Typography>
                          </Box>
                        </Paper>
                      ) : isPendingValidation ? (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#FEF3C7',
                            border: '1px dashed #D97706',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <WarningAmber
                            sx={{ color: '#D97706', fontSize: 24 }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                color: '#92400E',
                                fontSize: '0.875rem',
                                fontWeight: 800,
                              }}
                            >
                              Réservation en attente de validation du local SOA
                            </Typography>
                            <Typography
                              sx={{
                                color: '#B45309',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                              }}
                            >
                              Cette séance en présentiel dans les locaux SOA
                              doit être confirmée par le super admin avant
                              l'attribution définitive du cabinet.
                            </Typography>
                          </Box>
                        </Paper>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#ECFDF5',
                            border: '1px solid #10B981',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <CheckCircleOutline
                            sx={{ color: '#059669', fontSize: 24 }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                color: '#065F46',
                                fontSize: '0.875rem',
                                fontWeight: 800,
                              }}
                            >
                              Cabinet réservé et validé (Locaux SOA)
                            </Typography>
                            <Typography
                              sx={{
                                color: '#047857',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                              }}
                            >
                              La réservation du local SOA pour cette séance a
                              été officiellement confirmée par l'administration
                              SOA.
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <ClientCard
                        client={data?.client}
                        isIntervenant={isIntervenantUser}
                        userPractitioner={data?.user}
                        token={token}
                        onOpenPatientDialog={(patientProfileId) =>
                          setSelectedPatientId(patientProfileId)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <PractitionerCard user={data?.user} apiUrl={apiUrl} />
                    </Grid>

                    {hasAidants && (
                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: '16px',
                            bgcolor: '#F0F7FF',
                            border: '1px solid #BAE6FD',
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 800,
                              color: '#0369A1',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <SupervisorAccount sx={{ color: '#0284C7' }} />
                            Aidants & Personnes de Confiance rattachés
                          </Typography>

                          <Grid container spacing={2}>
                            {aidantsArray.map((aidant, idx) => {
                              const isPrincipal = Boolean(
                                aidant.pivot?.est_principal
                              );
                              const lien =
                                aidant.pivot?.lien_patient_aidant || 'Proche';

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                  key={aidant.id_aidant || idx}
                                >
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      bgcolor: '#FFFFFF',
                                      borderRadius: '12px',
                                      border: '1px solid #E2E8F0',
                                      height: '100%',
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1.5}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 800,
                                          color: '#1E293B',
                                        }}
                                      >
                                        {aidant.prenom_aidant}{' '}
                                        {aidant.nom_aidant}
                                      </Typography>
                                      <Stack direction="row" spacing={0.5}>
                                        <Chip
                                          label={lien}
                                          size="small"
                                          sx={{
                                            bgcolor: '#E0F2FE',
                                            color: '#0369A1',
                                            fontWeight: 700,
                                            height: 22,
                                            fontSize: '0.725rem',
                                          }}
                                        />
                                        {isPrincipal && (
                                          <Chip
                                            icon={
                                              <Star
                                                sx={{
                                                  fontSize: '12px!important',
                                                  color: '#D97706!important',
                                                }}
                                              />
                                            }
                                            label="Principal"
                                            size="small"
                                            sx={{
                                              bgcolor: '#FEF3C7',
                                              color: '#92400E',
                                              fontWeight: 800,
                                              height: 22,
                                              fontSize: '0.725rem',
                                            }}
                                          />
                                        )}
                                      </Stack>
                                    </Stack>

                                    <Stack spacing={0.8}>
                                      {aidant.telephone_aidant && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#475569',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontSize: '0.825rem',
                                          }}
                                        >
                                          <Phone
                                            fontSize="small"
                                            sx={{
                                              color: '#94A3B8',
                                              fontSize: 16,
                                            }}
                                          />{' '}
                                          {aidant.telephone_aidant}
                                        </Typography>
                                      )}
                                      {aidant.email_aidant && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#475569',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontSize: '0.825rem',
                                          }}
                                        >
                                          <Email
                                            fontSize="small"
                                            sx={{
                                              color: '#94A3B8',
                                              fontSize: 16,
                                            }}
                                          />{' '}
                                          {aidant.email_aidant}
                                        </Typography>
                                      )}
                                      {aidant.profession_aidant && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#64748B',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontSize: '0.825rem',
                                          }}
                                        >
                                          <Work
                                            fontSize="small"
                                            sx={{
                                              color: '#94A3B8',
                                              fontSize: 16,
                                            }}
                                          />{' '}
                                          {aidant.profession_aidant}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Paper>
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <ChatReservation
                        reservationId={data?.id_offre_reservation}
                        token={token}
                        apiUrl={apiUrl}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <OfferDetails infoOffre={infoOffre} />
                    </Grid>
                  </Grid>
                </Stack>
              )}

              {/* ONGLET 1 : COMPTES RENDUS MÉDICAUX */}
              {!isIntervenantUser && currentTab === 1 && (
                <ReservationReportsTab
                  reservation={reservation}
                  user={user}
                  apiUrl={apiUrl}
                  token={token}
                />
              )}

              {/* ONGLET 2 : DÉTAILS DE LA COMMANDE */}
              {!isIntervenantUser && currentTab === 2 && (
                <ReservationOrderTab
                  commande={data?.commande}
                  reservation={data}
                  apiUrl={apiUrl}
                  token={token}
                  userRole={user?.role_label || user?.role_user}
                  onOrderUpdated={handleOrderUpdated}
                />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {selectedPatientId && (
        <ViewPatientDialog
          open={!!selectedPatientId}
          patientId={selectedPatientId}
          onClose={() => setSelectedPatientId(null)}
          apiUrl={apiUrl}
          token={token}
        />
      )}
    </>
  );
};

export default ReservationDetailsDialog;